import {
  Card,
  Stack,
  useTheme,
  CardHeader,
  Typography,
  IconButton,
  CardContent,
  Grid2 as Grid,
} from '@mui/material';
import type { User } from 'oidc-client-ts';
import type { Dispatch, SetStateAction } from 'react';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { Iconify } from 'src/components/iconify';
import { useTranslate } from 'src/locales';
import type { IClient } from 'src/types/clients';

import { EditInformation } from './edit/EditInformation';
import * as yup from 'yup';
import type { FormikValues, FormikHelpers } from 'formik';
import { useUpdateClientMutation } from 'src/store/slices/clientsAPISlice';

import { toast } from 'src/components/snackbar';
import { Assets } from './edit/Assets';
import { reformatErrors } from 'src/utils/helpers';
import { Permission } from 'src/assets/data/permissions';

type Props = {
  clientData: IClient;
  user: User & {
    profile: {
      clientId: string;
      permissions: Permission[];
    };
  };
  onManuallyUpdateAsset: Dispatch<
    SetStateAction<{
      logo: string;
      banner: string;
    }>
  >;
};

export function ProfileTabView({ clientData, user, onManuallyUpdateAsset }: Props) {
  const theme = useTheme();
  const { currentLang, t } = useTranslate();

  // Update client
  const [updateClientGeneralInformation, { isLoading: updateClientGeneralInformationLoading }] =
    useUpdateClientMutation();
  const [updateClientLegalEntity, { isLoading: updateClientLegalEntityLoading }] =
    useUpdateClientMutation();

  // Edit mode state
  const [editMode, setEditMode] = useState({
    generalInformation: false,
    legalInformation: false,
    media: false,
  });

  // General information form values
  const [generalInformationValues, setGeneralInformationValues] = useState({
    name: clientData.name,
    zipCode: clientData.zipCode,
    countryCode: clientData.countryCode,
    city: clientData.city,
    visibleContactPhone: clientData.visibleContactPhone,
    address1: clientData.address1,
    visibleContactAddress: clientData.visibleContactAddress,
    techEmailAddress: clientData.techEmailAddress,
    website: clientData.website,
  });

  // General information validations
  const generalInformationValidations = useMemo(
    () =>
      yup.object().shape({
        name: yup
          .string()
          .max(100, t('validations.max', { field: t('forms.labels.name'), length: 100 }))
          .required(t('validations.required', { field: t('forms.labels.name') })),
        visibleContactAddress: yup
          .string()
          .max(50, t('validations.max', { field: t('forms.labels.email'), length: 50 }))
          .email(t('errors.invalidEmail'))
          .required(t('validations.required', { field: t('forms.labels.email') })),
        techEmailAddress: yup
          .string()
          .max(50, t('validations.max', { field: t('forms.labels.techEmailAddress'), length: 50 }))
          .email(t('errors.invalidEmail'))
          .required(t('validations.required', { field: t('forms.labels.techEmailAddress') })),
        address1: yup
          .string()
          .max(60, t('validations.max', { field: t('forms.labels.address'), length: 60 }))
          .required(t('validations.required', { field: t('forms.labels.address') })),
        website: yup
          .string()
          .url(t('validations.invalidURL', { field: t('forms.labels.website') }))
          .max(50, t('validations.max', { field: t('forms.labels.website'), length: 50 }))
          .required(t('validations.required', { field: t('forms.labels.website') })),
      }),
    [currentLang, t]
  );

  // Legal Information form values
  const [legalInformationValues, setLegalInformationValues] = useState({
    orgNr: clientData.orgNr,
    legalEntity: clientData.legalEntity,
    vatNo: clientData.vatNo,
  });

  // Set values for general information and legal information
  useEffect(() => {
    if (clientData) {
      setGeneralInformationValues({
        name: clientData.name,
        zipCode: clientData.zipCode,
        countryCode: clientData.countryCode,
        city: clientData.city,
        visibleContactPhone: clientData.visibleContactPhone,
        address1: clientData.address1,
        visibleContactAddress: clientData.visibleContactAddress,
        techEmailAddress: clientData.techEmailAddress,
        website: clientData.website,
      });
      setLegalInformationValues({
        orgNr: clientData.orgNr,
        legalEntity: clientData.legalEntity,
        vatNo: clientData.vatNo,
      });
    }
  }, [clientData]);

  // Legal information validations
  const legalInformationValidations = useMemo(
    () =>
      yup.object().shape({
        legalEntity: yup
          .string()
          .max(100, t('validations.max', { field: t('forms.labels.legalEntity'), length: 100 }))
          .required(t('validations.required', { field: t('forms.labels.legalEntity') })),
        orgNr: yup
          .string()
          .max(20, t('validations.max', { field: t('forms.labels.orgID'), length: 20 }))
          .required(t('validations.required', { field: t('forms.labels.orgID') })),
        vatNo: yup
          .string()
          .max(15, t('validations.max', { field: t('forms.labels.vatNo'), length: 15 }))
          .required(t('validations.required', { field: t('forms.labels.vatNo') })),
      }),
    [currentLang, t]
  );

  // Toggle edit mode
  const handleToggleEditMode = useCallback(
    (type: keyof typeof editMode, setValue: boolean | null = null) => {
      setEditMode((prev) => {
        return {
          ...prev,
          [type]: setValue !== null ? setValue : !prev[type],
        } as typeof editMode;
      });
    },
    []
  );

  // Handle save general information
  const handleSaveGeneralInformation = async (
    values: FormikValues,
    { setErrors }: FormikHelpers<typeof generalInformationValues>
  ) => {
    try {
      await updateClientGeneralInformation({
        id: clientData.id,
        body: values,
      })
        .unwrap()
        .then(() => {
          toast.success(t('toastMessages.clientUpdatedSuccesfull'), { position: 'bottom-left' });
        });
    } catch (error: any) {
      setErrors(reformatErrors(error.data.fieldErrors));
    }
  };

  // Handle save legal information
  const handleSaveLegalInformation = async (
    values: FormikValues,
    { setErrors }: FormikHelpers<typeof legalInformationValues>
  ) => {
    try {
      await updateClientLegalEntity({
        id: clientData.id,
        body: values,
      })
        .unwrap()
        .then(() => {
          toast.success(t('toastMessages.clientUpdatedSuccesfull'), { position: 'bottom-left' });
        });
    } catch (error: any) {
      setErrors(reformatErrors(error.data.fieldErrors));
    }
  };

  const canUpdate = useMemo(
    () => user.profile.permissions.includes(Permission.UpdateClient),
    [user]
  );

  return (
    <Grid container spacing={theme.spacing(4)} sx={{ position: 'relative' }}>
      <Grid size={canUpdate ? 5 : 12}>
        <Grid
          container
          spacing={2.5}
          direction={canUpdate ? 'row' : 'column'}
          sx={{ position: 'sticky', top: '100px' }}
        >
          {/* General Information card */}
          <Grid size={canUpdate ? 12 : 6}>
            <Card>
              <CardHeader
                title={
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{t('common.generalInfo')}</Typography>
                    {canUpdate && (
                      <IconButton
                        color="default"
                        sx={{
                          borderRadius: '8px',
                        }}
                        onClick={() => handleToggleEditMode('generalInformation')}
                        data-test-id="edit-row-link"
                      >
                        <Iconify icon="lucide:pencil" color={theme.palette.text.secondary} />
                      </IconButton>
                    )}
                  </Stack>
                }
                sx={{
                  padding: theme.spacing(3),
                  paddingRight: theme.spacing(2),
                }}
              />
              <CardContent sx={{ pt: 0, pb: theme.spacing(3) }}>
                {editMode.generalInformation ? (
                  <EditInformation<typeof generalInformationValues>
                    onCancel={() => {
                      handleToggleEditMode('generalInformation');
                    }}
                    values={generalInformationValues}
                    validations={generalInformationValidations}
                    onSave={handleSaveGeneralInformation}
                    isLoading={updateClientGeneralInformationLoading}
                    formId="general-information"
                    columns={[
                      {
                        name: 'name',
                        type: 'text',
                        placeholder: t('tables.columns.clientName'),
                        label: t('tables.columns.clientName'),
                      },
                      {
                        name: 'countryCode',
                        type: 'countrySelect',
                        placeholder: t('forms.labels.country'),
                        label: t('forms.labels.country'),
                      },
                      {
                        name: 'zipCode',
                        type: 'text',
                        placeholder: t('forms.labels.zip'),
                        label: t('forms.labels.zip'),
                      },
                      {
                        name: 'city',
                        type: 'text',
                        placeholder: t('forms.labels.city'),
                        label: t('forms.labels.city'),
                      },
                      {
                        name: 'address1',
                        type: 'text',
                        placeholder: t('common.address'),
                        label: t('common.address'),
                      },
                      {
                        name: 'visibleContactAddress',
                        type: 'text',
                        placeholder: t('common.email'),
                        label: t('common.email'),
                      },
                      {
                        name: 'visibleContactPhone',
                        type: 'phone',
                        placeholder: t('common.phone'),
                        label: t('common.phone'),
                      },
                      {
                        name: 'techEmailAddress',
                        type: 'text',
                        placeholder: t('common.clientTechSupportMail'),
                        label: t('common.clientTechSupportMail'),
                      },
                      {
                        name: 'website',
                        type: 'text',
                        placeholder: t('forms.labels.website'),
                        label: t('forms.labels.website'),
                      },
                    ]}
                  />
                ) : (
                  <>
                    {/* Client name */}
                    <Stack direction="row" sx={{ marginBottom: theme.spacing(1.5) }}>
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, textAlign: 'left', color: theme.palette.grey['600'] }}
                      >
                        {t('tables.columns.clientName')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textAlign: 'right',
                          color: theme.palette.warning.contrastText,
                        }}
                      >
                        {clientData.name}
                      </Typography>
                    </Stack>
                    {/* ID */}
                    <Stack direction="row" sx={{ marginBottom: theme.spacing(1.5) }}>
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, textAlign: 'left', color: theme.palette.grey['600'] }}
                      >
                        {t('common.id')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textAlign: 'right',
                          color: theme.palette.warning.contrastText,
                        }}
                      >
                        {clientData.id}
                      </Typography>
                    </Stack>
                    {/* Country */}
                    <Stack direction="row" sx={{ marginBottom: theme.spacing(1.5) }}>
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, textAlign: 'left', color: theme.palette.grey['600'] }}
                      >
                        {t('forms.labels.country')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textAlign: 'right',
                          color: theme.palette.warning.contrastText,
                        }}
                      >
                        {clientData.countryCode}
                      </Typography>
                    </Stack>
                    {/* ZIP Code */}
                    <Stack direction="row" sx={{ marginBottom: theme.spacing(1.5) }}>
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, textAlign: 'left', color: theme.palette.grey['600'] }}
                      >
                        {t('forms.labels.zip')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textAlign: 'right',
                          color: theme.palette.warning.contrastText,
                        }}
                      >
                        {clientData.zipCode}
                      </Typography>
                    </Stack>
                    {/* City */}
                    <Stack direction="row" sx={{ marginBottom: theme.spacing(1.5) }}>
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, textAlign: 'left', color: theme.palette.grey['600'] }}
                      >
                        {t('forms.labels.city')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textAlign: 'right',
                          color: theme.palette.warning.contrastText,
                        }}
                      >
                        {clientData.city}
                      </Typography>
                    </Stack>
                    {/* Address */}
                    <Stack direction="row" sx={{ marginBottom: theme.spacing(1.5) }}>
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, textAlign: 'left', color: theme.palette.grey['600'] }}
                      >
                        {t('common.address')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textAlign: 'right',
                          color: theme.palette.warning.contrastText,
                        }}
                      >
                        {clientData.address1}
                      </Typography>
                    </Stack>
                    {/* Phone number */}
                    <Stack direction="row" sx={{ marginBottom: theme.spacing(1.5) }}>
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, textAlign: 'left', color: theme.palette.grey['600'] }}
                      >
                        {t('common.phone')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textAlign: 'right',
                          color: theme.palette.warning.contrastText,
                        }}
                      >
                        {clientData.visibleContactPhone}
                      </Typography>
                    </Stack>
                    {/* Email */}
                    <Stack direction="row" sx={{ marginBottom: theme.spacing(1.5) }}>
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, textAlign: 'left', color: theme.palette.grey['600'] }}
                      >
                        {t('common.email')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textAlign: 'right',
                          color: theme.palette.warning.contrastText,
                        }}
                      >
                        {clientData.visibleContactAddress}
                      </Typography>
                    </Stack>
                    {/* Tech support email */}
                    <Stack direction="row" sx={{ marginBottom: theme.spacing(1.5) }}>
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, textAlign: 'left', color: theme.palette.grey['600'] }}
                      >
                        {t('common.clientTechSupportMail')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textAlign: 'right',
                          color: theme.palette.warning.contrastText,
                        }}
                      >
                        {clientData.techEmailAddress}
                      </Typography>
                    </Stack>
                    {/* Website URL */}
                    <Stack direction="row">
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, textAlign: 'left', color: theme.palette.grey['600'] }}
                      >
                        {t('forms.labels.website')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textAlign: 'right',
                          color: theme.palette.warning.contrastText,
                        }}
                      >
                        {clientData.website}
                      </Typography>
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Legal information card */}
          <Grid size={canUpdate ? 12 : 6}>
            <Card>
              <CardHeader
                title={
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{t('common.legalInfo')}</Typography>
                    {canUpdate && (
                      <IconButton
                        color="default"
                        sx={{
                          borderRadius: '8px',
                        }}
                        onClick={() => handleToggleEditMode('legalInformation')}
                        data-test-id="edit-row-link"
                      >
                        <Iconify icon="lucide:pencil" color={theme.palette.text.secondary} />
                      </IconButton>
                    )}
                  </Stack>
                }
                sx={{
                  padding: theme.spacing(3),
                  paddingRight: theme.spacing(2),
                }}
              />
              <CardContent sx={{ pt: 0, pb: theme.spacing(3) }}>
                {editMode.legalInformation ? (
                  <EditInformation<typeof legalInformationValues>
                    onCancel={() => handleToggleEditMode('legalInformation')}
                    values={legalInformationValues}
                    validations={legalInformationValidations}
                    onSave={handleSaveLegalInformation}
                    isLoading={updateClientLegalEntityLoading}
                    formId="legal-information"
                    columns={[
                      {
                        name: 'orgNr',
                        type: 'text',
                        placeholder: t('tables.columns.clientName'),
                        label: t('forms.labels.orgID'),
                      },
                      {
                        name: 'legalEntity',
                        type: 'text',
                        placeholder: t('tables.columns.clientName'),
                        label: t('forms.labels.legalEntity'),
                      },
                      {
                        name: 'vatNo',
                        type: 'text',
                        placeholder: t('tables.columns.clientName'),
                        label: t('forms.labels.vatNo'),
                      },
                    ]}
                  />
                ) : (
                  <>
                    {/* Organization ID */}
                    <Stack direction="row" sx={{ marginBottom: theme.spacing(1.5) }}>
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, textAlign: 'left', color: theme.palette.grey['600'] }}
                      >
                        {t('forms.labels.orgID')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textAlign: 'right',
                          color: theme.palette.warning.contrastText,
                        }}
                      >
                        {clientData.orgNr}
                      </Typography>
                    </Stack>
                    {/* Legal Entity */}
                    <Stack direction="row" sx={{ marginBottom: theme.spacing(1.5) }}>
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, textAlign: 'left', color: theme.palette.grey['600'] }}
                      >
                        {t('forms.labels.legalEntity')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textAlign: 'right',
                          color: theme.palette.warning.contrastText,
                        }}
                      >
                        {clientData.legalEntity}
                      </Typography>
                    </Stack>
                    {/* Vat NO */}
                    <Stack direction="row" sx={{ marginBottom: theme.spacing(1.5) }}>
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, textAlign: 'left', color: theme.palette.grey['600'] }}
                      >
                        {t('forms.labels.vatNo')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textAlign: 'right',
                          color: theme.palette.warning.contrastText,
                        }}
                      >
                        {clientData.vatNo}
                      </Typography>
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      {/* Assets card */}
      {canUpdate && (
        <Assets
          isClientOperator={canUpdate}
          onToggleEditMode={(setValue?: boolean | null) => handleToggleEditMode('media', setValue)}
          isEditModeEnabled={editMode['media']}
          logoUri={clientData['logo_uri']}
          bannerUri={clientData['banner_uri']}
          bannerForPdfUri={clientData['banner_for_pdf_uri']}
          clientName={clientData['name']}
          clientId={clientData['id']}
          brandColor={clientData['brandColor']}
          formId="assets"
          onManuallyUpdateAsset={onManuallyUpdateAsset}
        />
      )}
    </Grid>
  );
}
